import React from 'react';
import humanizeDuration from 'humanize-duration';
import Form from 'react-bootstrap/Form';
import parseDuration from 'parse-duration';
import HTMLRichTextEditor from '@/components/rich_text_editor/HTMLRichTextEditor';
import {Duration} from 'luxon';
import i18n from 'i18n';

interface FormValues {
    params: {
        title?: string;
        description?: string;
        timeout?: string;
    };
}

interface FormikProps {
    values: FormValues;
    setFieldValue: (field: string, value: any) => void;
    handleChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    handleBlur: (e: React.FocusEvent) => void;
    touched: {
        params?: {
            title?: boolean;
            timeout?: boolean;
        };
    };
    errors: {
        params?: {
            title?: string;
            timeout?: string;
        };
    };
}

interface ActionAssignBaseFormFieldsProps {
    formik: FormikProps;
    setTimeoutValidity: (valid: boolean) => void;
}

const ActionAssignBaseFormFields: React.FC<ActionAssignBaseFormFieldsProps> = React.memo((
    {
        formik,
        setTimeoutValidity,
    },
) => {
    const [timeoutInputValue, setTimeoutInputValue] = React.useState<string>(
        formik.values.params.timeout
            ? humanizeDuration(Duration.fromISO(formik.values.params.timeout).as('milliseconds'))
            : '',
    );
    const [timeoutIsValid, setTimeoutIsValid] = React.useState<boolean>(false);

    const timeoutAsMillis = (): number => Duration.fromISO(formik.values.params.timeout || '').as('milliseconds');

    const formatDuration = (): void => {
        const timeout = timeoutAsMillis();

        if (timeout) {
            setTimeoutInputValue(humanizeDuration(timeout));
            setTimeoutIsValid(true);
            setTimeoutValidity(true);
        } else if (!timeout && timeoutInputValue) {
            setTimeoutIsValid(false);
            setTimeoutValidity(false);
        }
    };

    return (
        <>
            <Form.Group className="mb-3" controlId="params.title" >
                <Form.Label>{i18n.t('jt_task_title.text')}</Form.Label>
                <Form.Control
                    type="text"
                    name="params.title"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.params.title || ''}
                    isInvalid={formik.touched.params?.title && !!formik.errors.params?.title}
                    data-cy="action-task-title-input"
                />
                <Form.Control.Feedback type="invalid">
                    {formik.errors.params?.title}
                </Form.Control.Feedback>
            </Form.Group >

            <Form.Group className="mb-3" controlId="params.description" >
                <Form.Label>{i18n.t('jt_task_description.text')}</Form.Label>
                <HTMLRichTextEditor
                    HTMLValue={formik.values.params.description || ''}
                    onChange={(HTMLDump: string) => formik.setFieldValue('params.description', HTMLDump)}
                />
            </Form.Group >

            <Form.Group className="mb-3" controlId="params.timeout" >
                <Form.Label>{i18n.t('jt_automatic_assignment.text')}</Form.Label>
                <Form.Control
                    type="text"
                    name="params.timeout"
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        const parsedDuration = parseDuration(e.target.value);
                        if (parsedDuration !== undefined && parsedDuration !== null) {
                            formik.setFieldValue(
                                'params.timeout',
                                Duration.fromMillis(parsedDuration).toJSON(),
                            );
                        }
                        setTimeoutInputValue(e.target.value);
                        setTimeoutIsValid(false);
                        setTimeoutValidity(false);
                    }}
                    onBlur={(e: React.FocusEvent) => {
                        formatDuration();
                        formik.handleBlur(e);
                    }}
                    value={timeoutInputValue}
                    placeholder="2 days, 5 hours"
                    isValid={timeoutIsValid}
                    isInvalid={!!(formik.touched.params?.timeout && (!timeoutAsMillis() && timeoutInputValue !== '0 seconds') && timeoutInputValue)}
                    data-cy="action-timeout-input"
                />
                <Form.Control.Feedback type="invalid">
                    {i18n.t('jt_please_enter.text')}
                </Form.Control.Feedback>
            </Form.Group >
        </>
    );
});

ActionAssignBaseFormFields.displayName = 'ActionAssignBaseFormFields';

export default ActionAssignBaseFormFields; 